import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  markModelsSeen,
  answerPrice,
  ModelAvatar,
  modelMatches,
  shortModelDesc,
  type LlmModel,
} from '@entities/llm';
import { useUserStore } from '@entities/user';
import { rub, sec } from '@shared/ui/v2';

interface Props {
  models: LlmModel[];
  assistantModelId: number | null;
  currentId: number | null;
  onApply: (m: LlmModel) => void;
  onClose: () => void;
  catalogHref: string;
}

/** Панель «Сравнить с другой моделью» (экран 4): поиск по всему каталогу, «Применить к тест-чату». */
export function ComparePanel({
  models,
  assistantModelId,
  currentId,
  onApply,
  onClose,
  catalogHref,
}: Props) {
  const uid = useUserStore((st) => st.user?.id);
  useEffect(() => markModelsSeen(uid), [uid]);
  const [tab, setTab] = useState<'rec' | 'all'>('rec');
  const [q, setQ] = useState('');
  const [limit, setLimit] = useState(30);
  const rec = models.filter((m) => m.verified);
  const price = (m: LlmModel) => answerPrice(m).value ?? Number.POSITIVE_INFINITY;
  // Поиск — по всему каталогу, независимо от вкладки. Порядок (решение Игоря 02.11):
  // сначала российские модели (Yandex, затем Рег.облако), ниже все остальные; внутри —
  // модель ассистента, рекомендованные, дешевле ответ.
  const found = (q.trim() ? models : tab === 'rec' ? rec : models)
    .filter((m) => modelMatches(m, q))
    .sort(
      (a, b) =>
        (a.group ?? 2) - (b.group ?? 2) ||
        Number(b.id === assistantModelId) - Number(a.id === assistantModelId) ||
        Number(b.verified) - Number(a.verified) ||
        price(a) - price(b),
    );
  const list = found.slice(0, limit);

  return (
    <aside aria-labelledby="h-cmp" className="tc-panel">
      <div className="tc-panel-head">
        <div
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}
        >
          <h2 id="h-cmp" className="v2-h2">
            Сравнить с другой моделью
          </h2>
          <button
            type="button"
            className="v2-icon-btn"
            style={{ width: 30, height: 30 }}
            onClick={onClose}
            aria-label="Закрыть"
            title="Закрыть"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
        <div className="v2-xs v2-muted">
          Выбранная модель отвечает только в тест-чате, диалог начнётся заново. Модель ассистента не
          меняется.
        </div>
        <div className="tc-psearch">
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#9CA3AF"
            strokeWidth="2"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="7" />
            <path d="M20 20l-3.5-3.5" />
          </svg>
          <label htmlFor="msearch" className="v2-sr">
            Поиск модели
          </label>
          <input
            id="msearch"
            type="search"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setLimit(30);
            }}
            placeholder={`Поиск по ${models.length} моделям: название, разработчик, задача`}
          />
        </div>
        <div role="tablist" aria-label="Какие модели показать" className="tc-ptabs">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'rec'}
            className={tab === 'rec' ? 'is-on' : ''}
            onClick={() => setTab('rec')}
          >
            Рекомендованные · {rec.length}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'all'}
            className={tab === 'all' ? 'is-on' : ''}
            onClick={() => setTab('all')}
          >
            Все модели · {models.length}
          </button>
        </div>
      </div>
      <div className="tc-plist">
        {list.map((m) => {
          const isCur = m.id === currentId;
          const isAsst = m.id === assistantModelId;
          const p = answerPrice(m);
          return (
            <div key={m.id} className={isCur ? 'tc-pcard is-cur' : 'tc-pcard'}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <ModelAvatar model={m} size={30} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontWeight: 700 }}>{m.title}</span>
                    {isAsst && <span className="mp-badge is-default">модель ассистента</span>}
                    {m.data_in_russia && <span className="mp-badge is-ru">данные в России</span>}
                  </div>
                  <div className="v2-xs v2-muted">
                    {m.vendor_title} · {shortModelDesc(m)}
                  </div>
                </div>
                <div style={{ textAlign: 'right', flex: 'none' }}>
                  <div style={{ fontWeight: 700 }}>
                    {p.value != null ? (p.approx ? '≈ ' : '') + rub(p.value) : '—'}
                  </div>
                  <div style={{ fontSize: 11 }} className="v2-muted">
                    за ответ
                  </div>
                </div>
              </div>
              {m.verified ? (
                <div className="tc-pmetrics">
                  <span>
                    Верных <b>{Math.round(m.eval_score ?? 0)}%</b>
                  </span>
                  <span>
                    Не выдумывает <b>{Math.round(m.eval_honesty ?? 0)}%</b>
                  </span>
                  <span>
                    Ответ <b>{sec(m.eval_seconds)}</b>
                  </span>
                </div>
              ) : (
                <div className="v2-xs" style={{ color: '#B45309' }}>
                  Не входит в рекомендованные — точность на ваших документах не подтверждена
                </div>
              )}
              <button
                type="button"
                className={isCur ? 'tc-papply is-cur' : 'tc-papply'}
                disabled={isCur}
                onClick={() => onApply(m)}
              >
                {isCur
                  ? 'Отвечает сейчас'
                  : isAsst
                    ? 'Вернуть модель ассистента'
                    : 'Начать диалог с этой моделью'}
              </button>
            </div>
          );
        })}
        {list.length === 0 && (
          <div className="v2-small v2-muted" style={{ padding: '8px 2px' }}>
            Ничего не нашлось. Попробуйте название разработчика: OpenAI, Google, Яндекс.
          </div>
        )}
        {limit < found.length && (
          <button
            type="button"
            className="v2-btn"
            style={{ alignSelf: 'center' }}
            onClick={() => setLimit((l) => l + 30)}
          >
            Показать ещё · {found.length - limit}
          </button>
        )}
        <div className="v2-xs v2-muted" style={{ padding: '2px 2px 0' }}>
          {q.trim()
            ? `Найдено: ${found.length} · поиск идёт по всем ${models.length} моделям`
            : tab === 'rec'
              ? 'Рекомендованы RiChat по проверке на реальных документах'
              : `Всего моделей: ${models.length}`}
        </div>
        <Link to={catalogHref} className="v2-link" style={{ padding: '2px 2px 8px' }}>
          Открыть полный каталог с фильтрами →
        </Link>
      </div>
    </aside>
  );
}
